import { getAddress } from '@ethersproject/address'
import { encodeFunctionData, type Abi } from 'viem'

import { ERC20_ABI } from '../../constants/abis/erc20'
import { abi as IUniswapV2PairABI } from '../../constants/abis/IUniswapV2Pair.json'
import MULTICALL_ABI from '../../constants/multicall/abi.json'
import { MULTICALL_NETWORKS } from '../../constants/multicall'
import { isNativeCurrency } from '../../config/native'
import { ChainId, Currency, Pair, Trade, WETH } from '../../sdk'
import { wrappedCurrency } from '../../utils/wrappedCurrency'

import { Call } from './actions'
import { toCallKey } from './actions'

const erc20Abi = ERC20_ABI as Abi
const pairAbi = IUniswapV2PairABI as Abi
const multicallAbi = MULTICALL_ABI as Abi

function normalizeCall(raw: Call): Call {
  const callData = raw.callData.startsWith('0x') ? raw.callData.toLowerCase() : `0x${raw.callData.toLowerCase()}`
  return { address: getAddress(raw.address), callData }
}

export function dedupeInvalidateCalls(calls: Call[]): Call[] {
  const seen = new Set<string>()
  const out: Call[] = []
  for (const raw of calls) {
    try {
      const c = normalizeCall(raw)
      const key = toCallKey(c)
      if (seen.has(key)) continue
      seen.add(key)
      out.push(c)
    } catch {
      // omitir entrada inválida
    }
  }
  return out
}

export function buildApproveInvalidateCalls(owner: string, tokenAddress: string, spender: string): Call[] {
  return dedupeInvalidateCalls([
    {
      address: getAddress(tokenAddress),
      callData: encodeFunctionData({
        abi: erc20Abi,
        functionName: 'allowance',
        args: [getAddress(owner), getAddress(spender)],
      }),
    },
  ])
}

export function buildTradeInvalidateCalls(chainId: ChainId, account: string, trade: Trade): Call[] {
  const calls: Call[] = []
  for (const t of trade.route.path) {
    calls.push({
      address: getAddress(t.address),
      callData: encodeFunctionData({
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [getAddress(account)],
      }),
    })
  }
  for (const pair of trade.route.pairs) {
    const addr = getAddress(pair.liquidityToken.address)
    calls.push({
      address: addr,
      callData: encodeFunctionData({
        abi: pairAbi,
        functionName: 'getReserves',
        args: [],
      }),
    })
  }
  if (isNativeCurrency(trade.route.input, chainId) || isNativeCurrency(trade.route.output, chainId)) {
    const mc = MULTICALL_NETWORKS[chainId]
    if (mc) {
      calls.push({
        address: getAddress(mc),
        callData: encodeFunctionData({
          abi: multicallAbi,
          functionName: 'getEthBalance',
          args: [getAddress(account)],
        }),
      })
    }
  }
  return dedupeInvalidateCalls(calls)
}

export function buildLiquidityChangeInvalidateCalls(
  chainId: ChainId,
  account: string,
  currencyA: Currency,
  currencyB: Currency,
): Call[] {
  const tokenA = wrappedCurrency(currencyA, chainId)
  const tokenB = wrappedCurrency(currencyB, chainId)
  if (!tokenA || !tokenB || tokenA.equals(tokenB)) return []

  const calls: Call[] = [
    {
      address: getAddress(tokenA.address),
      callData: encodeFunctionData({
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [getAddress(account)],
      }),
    },
    {
      address: getAddress(tokenB.address),
      callData: encodeFunctionData({
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [getAddress(account)],
      }),
    },
  ]

  const pairAddr = getAddress(Pair.getAddress(tokenA, tokenB))
  calls.push(
    {
      address: pairAddr,
      callData: encodeFunctionData({
        abi: pairAbi,
        functionName: 'getReserves',
        args: [],
      }),
    },
    {
      address: pairAddr,
      callData: encodeFunctionData({
        abi: erc20Abi,
        functionName: 'totalSupply',
        args: [],
      }),
    },
    {
      address: pairAddr,
      callData: encodeFunctionData({
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [getAddress(account)],
      }),
    },
  )

  if (isNativeCurrency(currencyA, chainId) || isNativeCurrency(currencyB, chainId)) {
    const mc = MULTICALL_NETWORKS[chainId]
    if (mc) {
      calls.push({
        address: getAddress(mc),
        callData: encodeFunctionData({
          abi: multicallAbi,
          functionName: 'getEthBalance',
          args: [getAddress(account)],
        }),
      })
    }
  }

  return dedupeInvalidateCalls(calls)
}

export function buildWrapInvalidateCalls(chainId: ChainId, account: string): Call[] {
  const calls: Call[] = [
    {
      address: getAddress(WETH[chainId].address),
      callData: encodeFunctionData({
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [getAddress(account)],
      }),
    },
  ]
  const mc = MULTICALL_NETWORKS[chainId]
  if (mc) {
    calls.push({
      address: getAddress(mc),
      callData: encodeFunctionData({
        abi: multicallAbi,
        functionName: 'getEthBalance',
        args: [getAddress(account)],
      }),
    })
  }
  return dedupeInvalidateCalls(calls)
}
